export type UploadState = 'preparing' | 'uploading' | 'retrying' | 'resuming' | 'completed' | 'cancelled';

export type ResumableVideoUploadProgress = {
  state: UploadState;
  confirmedBytes: number;
  totalBytes: number;
  speedBytesPerSecond: number | null;
};

type ApiResponse<T> = { data: T };
type ApiRequestConfig = { headers?: Record<string, string> };

export type ResumableUploadApi = {
  post: <T = unknown>(url: string, body?: unknown, config?: ApiRequestConfig) => Promise<ApiResponse<T>>;
  get: <T = unknown>(url: string, config?: ApiRequestConfig) => Promise<ApiResponse<T>>;
  delete: <T = unknown>(url: string, config?: ApiRequestConfig) => Promise<ApiResponse<T>>;
};

type UploadSession = {
  id: string;
  path: string;
  state: 'active' | 'completed' | 'promoted' | 'cancelled';
  chunk_size: number;
  session_url: string | null;
};

type StoredUpload = {
  id: string;
  fingerprint: string;
};

export type ResumableVideoUploadOptions = {
  api: ResumableUploadApi;
  apiBaseUrl: string;
  headers?: Record<string, string>;
  file: File;
  signal?: AbortSignal;
  onProgress?: (progress: ResumableVideoUploadProgress) => void;
  /** Used by tests; defaults to browser localStorage. */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** Used by tests; defaults to window.fetch. */
  fetchFn?: typeof fetch;
};

export type ResumableVideoUploadResult = {
  id: string;
  path: string;
};

const STORAGE_PREFIX = 'pacoca.resumable-video.';
const MAX_RETRIES = 5;
const FALLBACK_CHUNK_SIZE = 8 * 1024 * 1024;

class SessionExpiredError extends Error {}
class HttpStatusError extends Error {
  constructor(readonly status: number) {
    super(`Unexpected resumable upload status: ${status}`);
  }
}

const apiUrl = (baseUrl: string, path: string) => `${baseUrl.replace(/\/$/, '')}${path}`;

const storageFor = (storage?: ResumableVideoUploadOptions['storage']) => {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  return window.localStorage;
};

const fetchFor = (fetchFn?: typeof fetch) => {
  if (fetchFn) return fetchFn;
  if (typeof window === 'undefined') throw new Error('Fetch is unavailable.');
  return window.fetch.bind(window);
};

const fingerprintFor = (file: File) => [file.name, file.size, file.type, file.lastModified].join(':');
const storageKeyFor = (file: File) => `${STORAGE_PREFIX}${encodeURIComponent(fingerprintFor(file))}`;

const parseNextByte = (range: string | null, total: number): number => {
  if (!range) return 0;
  const match = /bytes=0-(\d+)/i.exec(range);
  if (!match) return 0;
  return Math.min(total, Number(match[1]) + 1);
};

const isAbort = (error: unknown, signal?: AbortSignal) =>
  signal?.aborted || (error instanceof DOMException && error.name === 'AbortError');

const isFetchFailure = (error: unknown) =>
  error instanceof TypeError && error.message === 'Failed to fetch';

const isRetriable = (error: unknown) =>
  !(error instanceof HttpStatusError) || error.status === 408 || error.status === 429 || error.status >= 500;

const statusFor = (error: unknown): number | null => {
  if (error instanceof HttpStatusError) return error.status;

  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === 'number' ? status : null;
};

const sleep = (milliseconds: number, signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
  const timeout = window.setTimeout(resolve, milliseconds);
  signal?.addEventListener('abort', () => {
    window.clearTimeout(timeout);
    reject(new DOMException('Upload cancelled', 'AbortError'));
  }, { once: true });
});

const emit = (
  callback: ResumableVideoUploadOptions['onProgress'],
  state: UploadState,
  confirmedBytes: number,
  totalBytes: number,
  speedBytesPerSecond: number | null,
) => callback?.({ state, confirmedBytes, totalBytes, speedBytesPerSecond });

async function queryGcsOffset(
  fetchFn: typeof fetch,
  sessionUrl: string,
  total: number,
  signal?: AbortSignal,
): Promise<number> {
  const response = await fetchFn(sessionUrl, {
    method: 'PUT',
    headers: { 'Content-Range': `bytes */${total}` },
    signal,
  });

  if (response.status === 308) return parseNextByte(response.headers.get('Range'), total);
  if (response.ok) return total;
  if (response.status === 404 || response.status === 410) throw new SessionExpiredError();
  throw new HttpStatusError(response.status);
}

async function sendChunk(
  fetchFn: typeof fetch,
  sessionUrl: string,
  file: File,
  start: number,
  end: number,
  signal?: AbortSignal,
): Promise<number | null> {
  const response = await fetchFn(sessionUrl, {
    method: 'PUT',
    body: file.slice(start, end + 1),
    headers: {
      'Content-Type': file.type || 'video/mp4',
      'Content-Range': `bytes ${start}-${end}/${file.size}`,
    },
    signal,
  });

  if (response.status === 308) return parseNextByte(response.headers.get('Range'), file.size);
  if (response.ok) return file.size;
  if (response.status === 404 || response.status === 410) throw new SessionExpiredError();
  throw new HttpStatusError(response.status);
}

/**
 * Uploads one browser File directly to a GCS resumable session. GCS's `Range`
 * header is queried before any chunk and after errors, so local progress is
 * never considered authoritative. The File itself is sliced per chunk and is
 * never copied wholesale into JavaScript memory.
 */
export async function uploadResumableVideo({
  api,
  apiBaseUrl,
  headers,
  file,
  signal,
  onProgress,
  storage,
  fetchFn: suppliedFetch,
}: ResumableVideoUploadOptions): Promise<ResumableVideoUploadResult> {
  const localStorage = storageFor(storage);
  const fetchFn = fetchFor(suppliedFetch);
  const storageKey = storageKeyFor(file);
  const requestConfig = headers ? { headers } : undefined;
  let session: UploadSession | null = null;
  let sessionId: string | null = null;

  const restoreOrStart = async (): Promise<UploadSession> => {
    const savedRaw = localStorage?.getItem(storageKey);
    if (savedRaw) {
      try {
        const saved = JSON.parse(savedRaw) as StoredUpload;
        if (saved.fingerprint === fingerprintFor(file)) {
          const { data } = await api.get<UploadSession>(
            apiUrl(apiBaseUrl, `/resumable-uploads/${saved.id}`),
            requestConfig,
          );
          if (data.state === 'completed' || data.state === 'promoted') return data;
          if (data.state === 'active' && data.session_url) return data;
        }
      } catch {
        // A removed/expired reservation is safely replaced below.
      }
    }

    const { data } = await api.post<UploadSession>(
      apiUrl(apiBaseUrl, '/resumable-uploads'),
      {
        type: 'video',
        size: file.size,
        content_type: file.type || 'video/mp4',
        file_name: file.name,
      },
      requestConfig,
    );
    return data;
  };

  const persist = (activeSession: UploadSession) => {
    localStorage?.setItem(storageKey, JSON.stringify({
      id: activeSession.id,
      fingerprint: fingerprintFor(file),
    } satisfies StoredUpload));
  };

  const restart = async (): Promise<UploadSession> => {
    emit(onProgress, 'resuming', 0, file.size, null);
    const { data } = await api.post<UploadSession>(
      apiUrl(apiBaseUrl, `/resumable-uploads/${sessionId}/restart`),
      undefined,
      requestConfig,
    );
    persist(data);
    return data;
  };

  const complete = async (): Promise<ResumableVideoUploadResult> => {
    if (!sessionId) throw new Error('A sessão de upload não está disponível.');

    const { data: completed } = await api.post<UploadSession>(
      apiUrl(apiBaseUrl, `/resumable-uploads/${sessionId}/complete`),
      undefined,
      requestConfig,
    );
    localStorage?.removeItem(storageKey);
    emit(onProgress, 'completed', file.size, file.size, null);

    return { id: completed.id, path: completed.path };
  };

  const completeAfterRestartConflict = async (error: unknown): Promise<ResumableVideoUploadResult> => {
    if (statusFor(error) !== 409) throw error;

    // A CORS-blocked final GCS response can still write the object. The restart
    // endpoint rejects that case to prevent overwriting it, so validate and
    // complete the already-received upload instead of displaying an error.
    return complete();
  };

  try {
    emit(onProgress, 'preparing', 0, file.size, null);
    session = await restoreOrStart();
    sessionId = session.id;
    persist(session);

    if (session.state === 'completed' || session.state === 'promoted') {
      localStorage?.removeItem(storageKey);
      emit(onProgress, 'completed', file.size, file.size, null);
      return { id: session.id, path: session.path };
    }
    if (!session.session_url) throw new Error('A sessão de upload não está disponível.');

    let confirmed: number;
    try {
      confirmed = await queryGcsOffset(fetchFn, session.session_url, file.size, signal);
    } catch (error) {
      // Existing browser storage can point to a session created before the
      // backend started forwarding the browser Origin to GCS. Such a session
      // surfaces only as an opaque TypeError in fetch, so replace it once and
      // retry the offset query with a newly-created CORS-aware session.
      if (!(error instanceof SessionExpiredError) && !isFetchFailure(error)) throw error;
      try {
        session = await restart();
      } catch (restartError) {
        return completeAfterRestartConflict(restartError);
      }
      sessionId = session.id;
      if (!session.session_url) throw new Error('A nova sessão de upload não está disponível.');
      confirmed = await queryGcsOffset(fetchFn, session.session_url, file.size, signal);
    }
    let lastConfirmed = confirmed;
    let lastConfirmedAt = Date.now();
    let retry = 0;
    const chunkSize = session.chunk_size || FALLBACK_CHUNK_SIZE;
    emit(onProgress, confirmed ? 'resuming' : 'uploading', confirmed, file.size, null);

    while (confirmed < file.size) {
      if (signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');
      const end = Math.min(file.size, confirmed + chunkSize) - 1;
      emit(onProgress, 'uploading', confirmed, file.size, null);

      try {
        const nextOffset = await sendChunk(fetchFn, session.session_url, file, confirmed, end, signal);
        confirmed = nextOffset ?? confirmed;
        retry = 0;
      } catch (error) {
        if (isAbort(error, signal)) throw error;

        try {
          confirmed = await queryGcsOffset(fetchFn, session.session_url, file.size, signal);
          if (confirmed >= file.size) break;
        } catch (statusError) {
          if (statusError instanceof SessionExpiredError) {
            try {
              session = await restart();
            } catch (restartError) {
              return completeAfterRestartConflict(restartError);
            }
            sessionId = session.id;
            if (!session.session_url) throw new Error('A nova sessão de upload não está disponível.');
            confirmed = 0;
            retry = 0;
            continue;
          }
          if (!isRetriable(statusError) || retry >= MAX_RETRIES) throw statusError;
        }

        if (!isRetriable(error) || retry >= MAX_RETRIES) throw error;
        retry += 1;
        emit(onProgress, 'retrying', confirmed, file.size, null);
        await sleep(Math.min(1_000 * 2 ** (retry - 1), 16_000), signal);
        continue;
      }

      const now = Date.now();
      const elapsed = Math.max(1, now - lastConfirmedAt);
      const speed = confirmed > lastConfirmed
        ? ((confirmed - lastConfirmed) * 1000) / elapsed
        : null;
      lastConfirmed = confirmed;
      lastConfirmedAt = now;
      emit(onProgress, confirmed >= file.size ? 'resuming' : 'uploading', confirmed, file.size, speed);
    }

    return complete();
  } catch (error) {
    if (isAbort(error, signal) && sessionId) {
      try {
        await api.delete(apiUrl(apiBaseUrl, `/resumable-uploads/${sessionId}`), requestConfig);
      } finally {
        localStorage?.removeItem(storageKey);
        emit(onProgress, 'cancelled', 0, file.size, null);
      }
    }
    throw error;
  }
}

export const formatUploadBytes = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 100 * 1024 * 1024 ? 0 : 1)} MB`;
};
